import { Icon } from '@ur-solutions/react-components'
import type { Column } from '@/components_deprecated'
import { parseISO } from 'date-fns'
import React, { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import styled, { css } from 'styled-components'
import type {
  TimesheetsDayReport,
  TimesheetsPeriodReport,
  TimesheetsSalaryWorkTime,
} from '../types.graphql'
import {
  TimesheetTableAbsenceRow,
  TimesheetTableAbsenceSubRow,
  TimesheetTableOvertimeSubRow,
  TimesheetTableRow,
  TimesheetTableSubRow,
} from './TimesheetTableRow/TimesheetTableRow'
import { TimesheetTableSummation } from './TimesheetTableSummations'
import { TimesheetTableShell } from './timesheet-table-shell'

interface InlineDropdownButtonProps {
  active: boolean
  isActiveRow: boolean
}

const InlineDropdownButton = styled.span<InlineDropdownButtonProps>`
  display: inline-block;
  position: absolute;
  background-color: ${props => props.theme.colors.white};
  height: 56px;
  width: 1.5rem;
  padding-top: 19px;
  padding-left: 0.275rem;
  content: '';
  margin: 0;
  cursor: pointer;
  /* Above the row it overlays, but below a sticky table header */
  z-index: 1;
  border-top: 1px solid ${props => props.theme.colors.gray10};
  border-bottom: 1px solid ${props => props.theme.colors.gray10};

  &:hover {
    background-color: ${props => props.theme.colors.gray10};
  }

  ${props =>
    !props.isActiveRow &&
    css`
      background-color: ${props.theme.colors.gray11};
      color: ${props.theme.colors.gray6} !important;
    `};
`
interface TimesheetsTableProps {
  periodReport: TimesheetsPeriodReport
  salaryWorkTime: TimesheetsSalaryWorkTime
  startDate: Date
  endDate: Date
  columns?: Column[]
  projectActive?: boolean
}

export const TimesheetsTable: React.FC<TimesheetsTableProps> = ({
  periodReport,
  salaryWorkTime,
  startDate,
  endDate,
  columns = [],
  projectActive = false,
}) => {
  const timesheetRows = useMemo(() => {
    return periodReport.dayReports.map(dayReport => (
      <TimesheetsDayReportRows
        key={dayReport.day}
        dayReport={dayReport}
        startDate={startDate}
        endDate={endDate}
        projectActive={projectActive}
        salaryWorkTime={salaryWorkTime}
      />
    ))
  }, [
    endDate,
    periodReport.dayReports,
    projectActive,
    salaryWorkTime,
    startDate,
  ])

  return (
    <TimesheetTableShell columns={columns}>
      {timesheetRows}

      <TimesheetTableSummation
        periodReport={periodReport}
        projectActive={projectActive}
      />
    </TimesheetTableShell>
  )
}

interface TimesheetsDayReportRowsProps {
  dayReport: TimesheetsDayReport
  salaryWorkTime: TimesheetsSalaryWorkTime
  startDate: Date
  endDate: Date
  projectActive?: boolean
}

const TimesheetsDayReportRows: React.FC<TimesheetsDayReportRowsProps> = ({
  dayReport,
  salaryWorkTime,
  startDate,
  endDate,
  projectActive = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const activeDropdown =
    dayReport.timeEntries.length +
      dayReport.absences.length +
      dayReport.overtimeEntries.length >
    1
  const isWithinInterval =
    parseISO(dayReport.day) >= startDate && parseISO(dayReport.day) <= endDate

  const icon = isExpanded ? 'chevron-down' : 'chevron-right'

  return (
    <>
      {activeDropdown && !isMobile && (
        <InlineDropdownButton
          active={isExpanded}
          isActiveRow={isWithinInterval}
          onClick={e => {
            if (activeDropdown) {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            } else {
              e.stopPropagation()
            }
          }}
        >
          <Icon
            margin={isExpanded ? '0 0.1rem' : '0 0.3rem'}
            size="10px"
            icon={icon}
          />
        </InlineDropdownButton>
      )}

      {dayReport.timeEntries.length > 0 || !dayReport.absences[0] ? (
        <TimesheetTableRow
          key={new Date(dayReport.day).getDay()}
          isWithinInterval={isWithinInterval}
          day={dayReport}
          projectActive={projectActive}
          salaryWorkTime={salaryWorkTime}
          onClick={() => {}}
        />
      ) : (
        <TimesheetTableAbsenceRow
          key={new Date(dayReport.day).getDay()}
          isWithinInterval={isWithinInterval}
          absence={dayReport.absences[0]}
          date={parseISO(dayReport.day)}
          projectActive={projectActive}
          salaryWorkTime={salaryWorkTime}
          onClick={() => {}}
        />
      )}

      {dayReport.timeEntries.length +
        dayReport.overtimeEntries.length +
        dayReport.absences.length >
        1 &&
        dayReport.timeEntries.map(timeEntry => (
          <TimesheetTableSubRow
            key={timeEntry.id}
            isWithinInterval={isWithinInterval}
            timeEntry={timeEntry}
            otherEntries={dayReport.timeEntries.filter(
              otherEntry =>
                otherEntry.id !== timeEntry.id &&
                parseISO(otherEntry.datetimeStart) <
                  parseISO(timeEntry.datetimeStart)
            )}
            hidden={!isExpanded}
            projectActive={projectActive}
            salaryWorkTime={salaryWorkTime}
            onClick={() => {}}
          />
        ))}

      {dayReport.overtimeEntries.map((overtimeEntry, idx) => (
        <TimesheetTableOvertimeSubRow
          key={idx}
          isWithinInterval={isWithinInterval}
          overtimeEntry={overtimeEntry}
          hidden={!isExpanded}
          projectActive={projectActive}
          onClick={() => {}}
        />
      ))}

      {dayReport.timeEntries.length > 0 &&
        dayReport.absences.map(absence => (
          <TimesheetTableAbsenceSubRow
            key={absence.id}
            isWithinInterval={isWithinInterval}
            absence={absence}
            hidden={!isExpanded}
            projectActive={projectActive}
            salaryWorkTime={salaryWorkTime}
            onClick={() => {}}
          />
        ))}
    </>
  )
}
